import React, { useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "../User/UserObject";
import { Routes } from "../../Routes";
import getCourseProgress from "../../util/getCourseProgress";
import { isSidebarCourseItemFocused } from "./menuCourseSelection";
import { type Course } from "./Menu.types";

import MenuProgressIndicator from "./MenuProgressIndicator";
import SidebarIcon from "./SidebarIcon";
import { sidebarIcons } from "./sidebarIcons";
import styles from "./MenuContentListItem.module.css";

type MenuListItemProps = {
  leftSide: React.ReactNode;
  rightSide: React.ReactNode;
  handleClick: () => void;
  className: string;
};

type MenuContentListItemProps = {
  course: Course;
  selectedCourse: Course | null;
  hideProgress: boolean | undefined;
  handleCourseClick: (course: Course) => void;
  isCertificateSection?: boolean;
  isExpiredItem?: boolean;
};

export function MenuListItem({
  leftSide,
  rightSide,
  handleClick,
  className,
}: MenuListItemProps) {
  const onClick = useCallback(() => {
    handleClick();
  }, [handleClick]);

  return (
    <li onClick={onClick} className={className}>
      <div className={styles.menuItemContainer}>
        <div className={styles.leftSide}>{leftSide}</div>
        <div className={styles.rightSide}>{rightSide}</div>
      </div>
    </li>
  );
}

export function MenuContentListItem({
  course,
  selectedCourse,
  hideProgress,
  handleCourseClick,
  isCertificateSection = false,
  isExpiredItem = false,
}: MenuContentListItemProps) {
  const { user } = useUser();
  const location = useLocation();
  const isFocused = isSidebarCourseItemFocused({
    pathname: location.pathname,
    courseId: course.ID,
    selectedCourseId: selectedCourse?.ID,
    isCertificateSection,
  });

  const listItemClassNames = [
    "menu-item",
    "clickable",
    isFocused ? "selected" : "",
    isExpiredItem ? styles.expiredItem : "",
  ]
    .filter(Boolean)
    .join(" ");

  const progress = getCourseProgress(course, user);
  const progressLabel = useMemo(() => {
    if (progress == null) {
      return null;
    }
    const clamped = Math.min(100, Math.max(0, Math.round(progress)));
    if (clamped >= 100) {
      return null;
    }
    if (clamped <= 0) {
      return "Ny";
    }
    return `${clamped}%`;
  }, [progress]);

  const onClick = useCallback(() => {
    handleCourseClick(course);
  }, [handleCourseClick, course]);

  // Navigate to certificate view for completed courses, education view for others
  const linkTo = useMemo(() => {
    if (isCertificateSection) {
      return `${Routes.Educations}/${course.ID}/certifikat`;
    }
    return Routes.Educations;
  }, [isCertificateSection, course.ID]);

  return (
    <Link to={linkTo}>
      <MenuListItem
        handleClick={onClick}
        className={listItemClassNames}
        leftSide={
          <>
            {!hideProgress && progress != null && (
              isExpiredItem ? (
                <SidebarIcon icon={sidebarIcons.expiredCourse} muted />
              ) : (
                <MenuProgressIndicator
                  progress={progress}
                  expired={isExpiredItem}
                  completedAsBrandDot={isCertificateSection}
                  selected={isFocused}
                />
              )
            )}
            <span className={styles.title}>{course.Title}</span>
          </>
        }
        rightSide={
          !hideProgress &&
          progressLabel != null && (
            <span className={styles.progressPercent}>{progressLabel}</span>
          )
        }
      />
    </Link>
  );
}
